import { useCallback, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import {
  securityGroupingCreateService,
  SECURITY_GROUPING_LIST_REFRESH_EVENT,
} from '@/api/domains/securityGrouping';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import {
  createSecurityGroupingFormSchema,
  type CreateSecurityGroupingFormValues,
} from '@/schemas/securityGrouping/createSecurityGroupingForm';


// ── Hook params ────────────────────────────────────────────────────────────

export interface UseCreateSecurityGroupingFormParams {
  onSubmit?: () => void;
  readOnly?: boolean;
}

// ── Return type interface ──────────────────────────────────────────────────

export interface UseCreateSecurityGroupingFormResult {
  codeValue: string;
  groupNameValue: string;
  sendsAttendanceStatusValue: boolean;
  handleCodeChange: (value: string) => void;
  handleGroupNameChange: (value: string) => void;
  handleSendsAttendanceStatusChange: (checked: boolean) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
  effectiveReadOnly: boolean;
  isSubmitting: boolean;
  isReady: boolean;
  errors: FieldErrors<CreateSecurityGroupingFormValues>;
  isSubmitted: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useCreateSecurityGroupingForm({
  onSubmit,
  readOnly,
}: UseCreateSecurityGroupingFormParams = {}): UseCreateSecurityGroupingFormResult {
  const { t: translate } = useTranslation();
  const { showNotification, closeSidePanel, showApiError } = useUI();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const effectiveReadOnly = !!readOnly;

  // ── React Hook Form ────────────────────────────────────────────────────
  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    watch,
    formState,
  } = useForm<CreateSecurityGroupingFormValues>({
    resolver: zodResolver(createSecurityGroupingFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
      groupName: '',
      sendsAttendanceStatus: false,
    },
  });

  // ── Reactive field values via watch() ──────────────────────────────────
  const codeValue = watch('code') || '';
  const groupNameValue = watch('groupName') || '';
  const sendsAttendanceStatusValue = watch('sendsAttendanceStatus') ?? false;

  // ── isReady ────────────────────────────────────────────────────────────
  const isReady = useMemo(
    () => groupNameValue.trim().length > 0,
    [groupNameValue],
  );

  // ── Field change handlers ──────────────────────────────────────────────
  const handleCodeChange = useCallback((value: string) => {
    setValue('code', value, { shouldValidate: true, shouldDirty: true });
  }, [setValue]);

  const handleGroupNameChange = useCallback((value: string) => {
    setValue('groupName', value, { shouldValidate: true, shouldDirty: true });
  }, [setValue]);

  const handleSendsAttendanceStatusChange = useCallback((checked: boolean) => {
    setValue('sendsAttendanceStatus', checked, { shouldValidate: true, shouldDirty: true });
  }, [setValue]);

  // ── Submit handler ─────────────────────────────────────────────────────
  const onValid = useCallback(
    async (data: CreateSecurityGroupingFormValues) => {
      if (readOnly) return;
      setIsSubmitting(true);

      const response = await securityGroupingCreateService.create({
        code: data.code.trim() || null,
        groupName: data.groupName,
        sendsAttendanceStatus: data.sendsAttendanceStatus,
      });

      if (response.success) {
        showNotification('success', translate('securityGrouping:messages.createSuccess'));
        window.dispatchEvent(new CustomEvent(SECURITY_GROUPING_LIST_REFRESH_EVENT));
        closeSidePanel();
        onSubmit?.();
      } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
        injectValidationErrors(response.error.validationErrors, { setError });
      } else {
        showApiError(response);
      }

      setIsSubmitting(false);
    },
    [readOnly, showNotification, showApiError, translate, closeSidePanel, setError, onSubmit],
  );

  const onInvalid = useCallback((): void => {
    // Intentionally empty — errors displayed via isSubmitted guard
  }, []);

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      if (effectiveReadOnly) return;

      const wrappedValid = (data: CreateSecurityGroupingFormValues) => { void onValid(data); };
      const wrappedInvalid = () => { onInvalid(); };
      const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
      void runner(event);
    },
    [rhfHandleSubmit, onValid, onInvalid, effectiveReadOnly],
  );

  return {
    codeValue,
    groupNameValue,
    sendsAttendanceStatusValue,
    handleCodeChange,
    handleGroupNameChange,
    handleSendsAttendanceStatusChange,
    handleSubmit,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    errors: formState.errors,
    isSubmitted: formState.isSubmitted,
  };
}
